/**************************************************************************************************/
/*   File: /src/app/stores/actions/editing/regulation.files.actions.ts                            */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Thursday May 2nd 2019                                                          */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Thu May 09 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Action } from '@ngrx/store';

import * as fromShared from '../../../shared';

/**
 * The file action types enum
 */
export enum RegulationFileActionTypes {
  /**
   * The file added action's identifier
   */
  REGULATION_FILE_ADDED = '[Editing][File] Regulation File added',
  /**
   * The file removed action's identifier
   */
  REGULATION_FILE_REMOVED = '[Editing][File] Regulation File removed',
  /**
   * The file removed success action's identifier
   */
  REGULATION_FILE_REMOVED_SUCCESS = '[Editing][File] Regulation File removed success',
  /**
   * The file removed fail action's identifier
   */
  REGULATION_FILE_REMOVED_FAIL = '[Editing][File] Regulation File removed fail',
  /**
   * The file clead action's identifier
   */
  REGULATION_FILE_CLEAR = '[Editing][File] Regulation File clear',
  /**
   * The file upload action's identifier
   */
  REGULATION_FILE_UPLOAD = '[Editing][File] Regulation File upload',
  /**
   * The file upload success action's identifier
   */
  REGULATION_FILE_UPLOAD_SUCCESS = '[Editing][File] Regulation File upload success',
  /**
   * The file upload failure action's identifier
   */
  REGULATION_FILE_UPLOAD_FAILURE = '[Editing][File] Regulation File upload fail'
}
/**
 * Action dispatched when a file is ready to be uploaded 
 * and added to the collection of file required for upload
 */
export class RegulationFileAddAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_ADDED;
  /**
   * The constructor
   * @param payload The payload of files and fileType
   */
  constructor(public payload: { files: Array<fromShared.models.FileModel>, type: string, areaCode: string }) { }
}
/**
 * Action dispatched when a file is removed
 * from the collection of file required for upload
 */
export class RegulationFileRemovedAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_REMOVED;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string, type: string, areaCode: string, isUploaded: boolean }) { }
}
/**
 * Action dispatched when a file is removed also from remote
 */
export class RegulationFileRemovedSuccessAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_REMOVED_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string, type: string, areaCode: string }) { }
}
/**
 * Action dispatched when a file cannot be removed from remote
 */
export class RegulationFileRemovedFailAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_REMOVED_FAIL;
  /**
   * The constructor
   * @param payload The payload is the message
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}
/**
 * Action dispatched when the collection of file required for upload
 * is resetted
 */
export class RegulationFileClearAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_CLEAR;
  /**
   * The constructor
   * @param payload The payload is the context
   */
  constructor(public payload: { files: fromShared.models.FileModel[], type: string, areaCode: string }) { }
}
/**
 * Action dispatched when user wants to upload all the files prepared in the collection
 * with other actions previously described
 */
export class RegulationFileUploadAction implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_UPLOAD;
  /**
   * The constructor
   * @param payload The payload is the single file and the fileType
   */
  constructor(public payload: { file: fromShared.models.FileModel, type: string, areaCode: string }) { }
}
/**
 * Action dispatched when file are correctly uploaded to the middleware
 */
export class RegulationFileUploadActionSuccess implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_UPLOAD_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the single file and the  fileType: fromShared.enums.FileType
   */
  constructor(public payload: {
    file: fromShared.models.FileModel,
    type: string,
    areaCode: string,
    documentId: fromShared.models.LocalDocumentId
  }) { }
}
/**
 * Action dispatched when file upload throws an exception
 */
export class RegulationFileUploadActionFail implements Action {
  /**
   * The type
   */
  readonly type = RegulationFileActionTypes.REGULATION_FILE_UPLOAD_FAILURE;

  /**
   * The constructor
   * @param payload The error to display and the context
   */
  constructor(public payload: {
    file: fromShared.models.FileModel, type: string,
    areaCode: string, message: fromShared.models.Message
  }) { }
}

/**
 * The file action type
 */
export type RegulationFileActions =
  RegulationFileAddAction |
  RegulationFileRemovedAction |
  RegulationFileRemovedSuccessAction |
  RegulationFileRemovedFailAction |
  RegulationFileClearAction |
  RegulationFileUploadAction |
  RegulationFileUploadActionSuccess |
  RegulationFileUploadActionFail;